import { type GridCellParams, type GridRenderCellParams, type GridRowId, type GridRowModel } from '@mui/x-data-grid-pro';
import { type DateFormatString, formatTimestamp, parseDate } from '../Date';
import type { InputFieldProps } from '../InputField';
import { getFieldFromArray } from '../Texte';
import { checkUndefinedNullOrEmpty, checkUndefinedOrNull } from '../Tools';
import { convertCheckValue } from '../inputfields/Check';
import { convertDateTimeValue } from '../inputfields/DateTime';
import InputImage from '../inputfields/Image';
import { convertNumberValue } from '../inputfields/Number';
import { convertPasswordValue } from '../inputfields/Password';
import { convertSelectValue } from '../inputfields/Select';
import { sInputfields } from '../inputfields/sInputFields';
import { type DataGridEnrichedColDef, type DataGridProps } from './types';

export function renderDataGridValue(
    params: Partial<GridRenderCellParams>,
    originalValue: any,
    colDef: DataGridEnrichedColDef,
    additionalSettingsRaw: {
        convertToNumber?: boolean;
        customFormatDateFormat?: DateFormatString;
        factor?: number;
        mwst?: number;
        parseDateFormat?: DateFormatString;
    } = { factor: 1 }
) {
    const additionalSettings = {
        ...additionalSettingsRaw,
        factor: additionalSettingsRaw?.factor || 1,
        mwst: additionalSettingsRaw?.mwst || 1,
    };

    if (!checkUndefinedOrNull(colDef?.renderCell)) {
        return colDef?.renderCell(params as GridRenderCellParams);
    } else if (colDef?.type === 'avatar' || colDef?.type === 'image' || colDef?.type === 'image360a' || colDef?.type === 'image360i') {
        return <InputImage {...colDef} v={originalValue} />;
    } else if (colDef?.type === 'singleselect' || colDef?.type === 'multiselect' || colDef?.type === 'radio') {
        const value = additionalSettings?.convertToNumber ? parseFloat(originalValue) : originalValue;
        const returnValue = convertSelectValue(value, colDef?.data, colDef?.type, colDef?.showExtra, colDef?.showField, colDef?.height, colDef?.testid, colDef?.id) || '-';
        return !checkUndefinedOrNull(returnValue) ? returnValue : '-';
    } else if (colDef?.type === 'year' || colDef?.type === 'month' || colDef?.type === 'date' || colDef?.type === 'datetime' || colDef?.type === 'datetimeSec' || colDef?.type === 'time') {
        const value = !checkUndefinedNullOrEmpty(additionalSettings?.parseDateFormat) ? formatTimestamp(parseDate(originalValue, additionalSettings?.parseDateFormat)) : originalValue;
        const returnValue = convertDateTimeValue(value, colDef?.type, additionalSettings?.customFormatDateFormat);
        return !checkUndefinedOrNull(returnValue) ? returnValue : '-';
    } else if (colDef?.type === 'currency' || colDef?.type === 'percent' || colDef?.type === 'decimal') {
        const value = additionalSettings?.convertToNumber ? parseFloat(originalValue) : originalValue;
        const returnValue =
            convertNumberValue(value * additionalSettings?.factor * (colDef?.type === 'currency' ? additionalSettings?.mwst : 1), colDef?.type, colDef?.unrounded, colDef?.einheit, colDef?.ca) || '-';
        return !checkUndefinedOrNull(returnValue) ? returnValue : '-';
    } else if (colDef?.type === 'check' || colDef?.type === 'switch') {
        const value = originalValue;
        const returnValue = convertCheckValue(value) || '-';
        return !checkUndefinedOrNull(returnValue) ? returnValue : '-';
    } else if (colDef?.type === 'password') {
        const value = originalValue;
        const returnValue = convertPasswordValue(value, colDef?.passCheck, colDef?.showPassTooltip) || '-';
        return !checkUndefinedOrNull(returnValue) ? returnValue : '-';
    } else if (colDef?.type === 'multiline') {
        const returnValue = originalValue;
        return <span style={{ whiteSpace: 'pre' }}>{!checkUndefinedOrNull(returnValue) ? returnValue : '-'}</span>;
    } else {
        const returnValue = originalValue;
        return !checkUndefinedOrNull(returnValue) ? returnValue : '-';
    }
}

export function formatDataGridValue(rowId: GridRowId, colDef: DataGridEnrichedColDef, value: any) {
    if (!checkUndefinedOrNull(colDef?.valueFormatter)) {
        return colDef?.valueFormatter({ api: undefined, field: colDef.field, id: rowId, value: value });
    } else if (colDef?.type === 'singleselect' || colDef?.type === 'multiselect' || colDef?.type === 'radio') {
        return convertSelectValue(value, colDef?.data, colDef?.type, colDef?.showExtra, 't', colDef?.height, colDef?.testid, colDef?.id);
    } else if (colDef?.type === 'year' || colDef?.type === 'month' || colDef?.type === 'date' || colDef?.type === 'datetime' || colDef?.type === 'datetimeSec' || colDef?.type === 'time') {
        return convertDateTimeValue(value, colDef?.type, undefined);
    } else if (colDef?.type === 'currency' || colDef?.type === 'percent' || colDef?.type === 'decimal') {
        return convertNumberValue(value, colDef?.type, colDef?.unrounded, colDef?.einheit, colDef?.ca);
    } else if (colDef?.type === 'check' || colDef?.type === 'switch') {
        return convertCheckValue(value);
    } else if (colDef?.type === 'password') {
        return convertPasswordValue(value, colDef?.passCheck, colDef?.showPassTooltip);
    } else {
        return value;
    }
}

export function renderDataGridEditValue(colDef: DataGridEnrichedColDef & InputFieldProps, rowId: GridRowId, row: GridRowModel) {
    const InputComponent = getFieldFromArray(sInputfields, 'component', colDef.type || 'text', 'i', undefined);
    if (!checkUndefinedOrNull(InputComponent)) {
        return <InputComponent edit v={!checkUndefinedNullOrEmpty(row) ? row[colDef.field] : undefined} {...colDef} />;
    }
    return 'rowId: ' + rowId + ', field: ' + colDef.field + ', type: ' + colDef.type;
}

export function filterDataGridValue(search: string, colDef: DataGridEnrichedColDef) {
    if (checkUndefinedNullOrEmpty(search)) {
        return undefined;
    }

    return (params: GridCellParams<any, any>): boolean => {
        let valueToUse: string;
        if (colDef?.type === 'singleselect' || colDef?.type === 'multiselect' || colDef?.type === 'radio') {
            const d = colDef?.data?.find((p) => p?.i === params?.value);
            valueToUse = `${d?.i || ''} ${d?.t || ''} ${d?.et || ''}`;
        } else if (colDef?.type === 'year' || colDef?.type === 'month' || colDef?.type === 'date' || colDef?.type === 'datetime' || colDef?.type === 'datetimeSec' || colDef?.type === 'time') {
            valueToUse = convertDateTimeValue(params?.value, colDef?.type, undefined);
        } else if (colDef?.type === 'currency' || colDef?.type === 'percent' || colDef?.type === 'decimal') {
            valueToUse = `${params?.value} ${convertNumberValue(params?.value, colDef?.type, colDef?.unrounded, colDef?.einheit, colDef?.ca)}`;
        } else if (colDef?.type === 'check' || colDef?.type === 'switch') {
            valueToUse = convertCheckValue(params?.value);
        } else if (colDef?.type === 'password') {
            valueToUse = convertPasswordValue(params?.value, colDef?.passCheck, false)?.toString();
        } else {
            valueToUse = params?.value?.toString();
        }

        if (checkUndefinedNullOrEmpty(valueToUse)) {
            return false;
        }
        return valueToUse?.toLowerCase()?.includes(search?.toString()?.toLowerCase());
    };
}

export function applySortFilter(params: {
    columns: DataGridProps['columns'];

    comparator: (a: any, b: any) => number;
    colSearch?: string;
    hideFields?: DataGridEnrichedColDef['field'][];
}) {
    const { columns, comparator, colSearch, hideFields } = params;

    let newColumns = [];

    const stabilizedThis = columns.map((el, index) => [el, index] as const);

    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });

    newColumns = stabilizedThis.map((el) => el[0]);

    if (!checkUndefinedNullOrEmpty(hideFields)) {
        newColumns = newColumns.filter((item: DataGridEnrichedColDef) => hideFields?.includes(item?.field));
    }

    if (!checkUndefinedNullOrEmpty(colSearch)) {
        newColumns = newColumns.filter(
            (item: DataGridEnrichedColDef) => item?.headerName?.toLowerCase().includes(colSearch?.toLowerCase()) || item?.group?.toLowerCase().includes(colSearch?.toLowerCase())
        );
    }

    return newColumns;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

export function getComparator<Key extends keyof any>(order: 'asc' | 'desc', orderBy: Key): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

export function emptyRows(page: number, rowsPerPage: number, arrayLength: number) {
    return page > 0 ? Math.max(0, (1 + page) * rowsPerPage - arrayLength) : 0;
}
